import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RoleBasedInsights = ({ selectedRole }) => {
  const roleContent = {
    CEO: {
      title: 'Strategic Impact Dashboard for CEOs',
      subtitle: 'Transform your global strategy with speed, certainty, and competitive advantage',
      icon: 'Target',
      color: 'bg-blue-600',
      keyMetrics: [
        {
          metric: '90 Days',
          label: 'Time to Market',
          detail: 'Operational GCC vs 18-24 months DIY',
          icon: 'Zap'
        },
        {
          metric: '3x Faster',
          label: 'Innovation Cycle',
          detail: 'Product development acceleration',
          icon: 'Rocket'
        },
        {
          metric: '6 Months',
          label: 'Asia Presence',
          detail: 'Market expansion timeline',
          icon: 'Globe2'
        },
        {
          metric: '100+',
          label: 'Pre-Vetted Leaders',
          detail: 'Access to top 1% talent in 8 weeks',
          icon: 'Users'
        }
      ],
      strategicOutcomes: [
        {
          outcome: 'Competitive Advantage',
          description: 'Establish 24/7 global operations before competitors',
          impact: 'First-mover advantage in key markets',
          icon: 'TrendingUp'
        },
        {
          outcome: 'Innovation Acceleration',
          description: 'Access world-class AI/ML and engineering talent',
          impact: '3x faster product development cycles',
          icon: 'Brain'
        },
        {
          outcome: 'Market Expansion',
          description: 'Strategic presence in Asia\'s fastest-growing markets',
          impact: 'New revenue streams within 6-12 months',
          icon: 'Map'
        },
        {
          outcome: 'Board Confidence',
          description: 'Comprehensive risk mitigation with full guarantees',
          impact: 'Zero board hesitation or approval delays',
          icon: 'Award'
        }
      ],
      decisionFramework: {
        title: 'CEO Decision Framework',
        questions: [
          {
            question: 'How fast can we move?',
            answer: 'Board-ready business case in 10 days. Operational in 90 days.',
            icon: 'Clock'
          },
          {
            question: 'What are the risks?',
            answer: 'Zero-risk approach with milestone-based compliance and contractual remediation if commitments are missed.',
            icon: 'Shield'
          },
          {
            question: 'Can we exit if needed?',
            answer: 'Multiple exit strategies built-in, including full BOT with ownership transfer.',
            icon: 'GitBranch'
          },
          {
            question: 'What\'s the strategic value?',
            answer: '24/7 innovation engine, 50-60% cost savings vs US operations (Zinnov 2024), access to 100+ leaders.',
            icon: 'Target'
          }
        ]
      },
      cta: {
        primary: 'Schedule CEO Briefing',
        secondary: 'Download Board Deck',
        tertiary: 'See Strategic Case Studies'
      }
    },
    CFO: {
      title: 'Financial Command Center for CFOs',
      subtitle: 'Predictable economics, transparent pricing, and measurable ROI',
      icon: 'DollarSign',
      color: 'bg-green-600',
      keyMetrics: [
        {
          metric: '40-60%',
          label: 'Cost Savings',
          detail: 'vs US operations with full transparency',
          icon: 'TrendingDown'
        },
        {
          metric: '8-12 Mo',
          label: 'ROI Timeline',
          detail: 'Typical break-even period',
          icon: 'BarChart'
        },
        {
          metric: '$0',
          label: 'Hidden Costs',
          detail: 'All-in transparent pricing',
          icon: 'Eye'
        },
        {
          metric: 'Fixed-rate',
          label: 'Hidden Cost Elimination',
          detail: 'No change-order creep vs DIY or traditional consultants',
          icon: 'PiggyBank'
        }
      ],
      strategicOutcomes: [
        {
          outcome: 'Budget Predictability',
          description: 'Fixed-rate packages with no surprise costs',
          impact: 'Zero budget overruns in 200+ implementations',
          icon: 'Receipt'
        },
        {
          outcome: 'TCO Transparency',
          description: 'Every cost itemized upfront with comparison models',
          impact: 'Full financial visibility from day one',
          icon: 'PieChart'
        },
        {
          outcome: 'ROI Acceleration',
          description: '90-day setup vs 18-24 month DIY approach',
          impact: 'Revenue generation 12-18 months earlier',
          icon: 'TrendingUp'
        },
        {
          outcome: 'Risk Mitigation',
          description: 'Performance guarantees with financial penalties',
          impact: 'Your investment is fully protected',
          icon: 'Shield'
        }
      ],
      decisionFramework: {
        title: 'CFO Financial Analysis',
        questions: [
          {
            question: 'What\'s the true all-in cost?',
            answer: 'Complete transparency: Setup $75K-150K. No hidden costs. Pass-through govt fees.',
            icon: 'DollarSign'
          },
          {
            question: 'When do we break even?',
            answer: 'Typical ROI in 8-12 months with 50-60% ongoing savings vs US operations (Zinnov GCC Cost Index 2024).',
            icon: 'Calculator'
          },
          {
            question: 'What about budget risk?',
            answer: 'Fixed-rate options available. Zero overruns in 200+ GCC setups.',
            icon: 'Lock'
          },
          {
            question: 'How does it compare?',
            answer: 'Material cost savings vs DIY. Faster time to value with parallel-track execution.',
            icon: 'GitCompare'
          }
        ]
      },
      cta: {
        primary: 'Calculate Your ROI',
        secondary: 'Get Detailed Pricing',
        tertiary: 'Review Financial Model'
      }
    },
    CIO: {
      title: 'Technology Excellence for CIOs',
      subtitle: 'Enterprise-grade security, modern architecture, and seamless integration',
      icon: 'Code',
      color: 'bg-purple-600',
      keyMetrics: [
        {
          metric: '99.9%',
          label: 'Uptime SLA',
          detail: 'Enterprise-grade infrastructure',
          icon: 'Server'
        },
        {
          metric: 'Zero',
          label: 'Security Incidents',
          detail: 'Across 200+ GCC implementations',
          icon: 'Shield'
        },
        {
          metric: '6 Weeks',
          label: 'Tech Stack Setup',
          detail: 'Zero-trust architecture included',
          icon: 'Zap'
        },
        {
          metric: 'SOC2 + ISO',
          label: 'Certifications',
          detail: 'Compliant from day one',
          icon: 'Award'
        }
      ],
      strategicOutcomes: [
        {
          outcome: 'Zero-Trust Security',
          description: 'Enterprise-grade security architecture from day one',
          impact: 'No security incidents in 200+ implementations',
          icon: 'Lock'
        },
        {
          outcome: 'Cloud-Native Stack',
          description: 'Modern infrastructure on AWS/Azure/GCP',
          impact: 'Seamless integration with existing systems',
          icon: 'Cloud'
        },
        {
          outcome: 'DevOps Maturity',
          description: 'CI/CD pipelines and automation built-in',
          impact: '3x faster deployment cycles',
          icon: 'GitPullRequest'
        },
        {
          outcome: 'AI/ML Capabilities',
          description: 'Access to cutting-edge ML/AI talent and infrastructure',
          impact: 'Innovation-ready platform from launch',
          icon: 'Brain'
        }
      ],
      decisionFramework: {
        title: 'CIO Technical Assessment',
        questions: [
          {
            question: 'What\'s the security posture?',
            answer: 'Zero-trust architecture, SOC2 + ISO 27001 certified. Zero incidents ever.',
            icon: 'Shield'
          },
          {
            question: 'How does it integrate?',
            answer: 'API-first design. Pre-built connectors for major systems. Full integration playbook.',
            icon: 'Link'
          },
          {
            question: 'What tech stack options?',
            answer: 'Your choice of cloud (AWS/Azure/GCP). Modern DevOps. AI/ML ready.',
            icon: 'Layers'
          },
          {
            question: 'What about compliance?',
            answer: 'GDPR, SOC2, ISO 27001, local regulations. All covered with guarantees.',
            icon: 'CheckCircle'
          }
        ]
      },
      cta: {
        primary: 'Review Tech Architecture',
        secondary: 'Security Assessment',
        tertiary: 'Integration Playbook'
      }
    }
  };

  if (!selectedRole || !roleContent[selectedRole]) {
    return null;
  }

  const content = roleContent[selectedRole];

  return (
    <section className="py-20 bg-gradient-to-br from-background to-muted/30">
      <div className="container-executive">
        {/* Role Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className={`inline-flex items-center space-x-3 ${content.color} text-white px-6 py-3 rounded-full text-sm font-medium mb-6`}>
            <Icon name={content.icon} size={20} />
            <span>Customized for {selectedRole}s</span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-headline font-bold text-text-primary mb-6">
            {content.title}
          </h2>

          <p className="text-xl text-text-secondary">
            {content.subtitle}
          </p>
        </div>

        {/* Key Metrics */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {content.keyMetrics.map((item, index) => (
            <div key={index} className="card-elevated rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 ${content.color} rounded-lg flex items-center justify-center`}>
                  <Icon name={item.icon} size={24} color="white" />
                </div>
              </div>
              <div className="text-3xl font-bold text-text-primary mb-2">{item.metric}</div>
              <div className="font-semibold text-text-primary mb-1">{item.label}</div>
              <div className="text-sm text-text-secondary">{item.detail}</div>
            </div>
          ))}
        </div>

        {/* Strategic Outcomes */}
        <div className="mb-16">
          <h3 className="text-3xl font-headline font-bold text-text-primary text-center mb-12">
            Strategic Outcomes That Matter to {selectedRole}s
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {content.strategicOutcomes.map((item, index) => (
              <div key={index} className="card-elevated rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <div className={`w-14 h-14 ${content.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <Icon name={item.icon} size={28} color="white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xl font-headline font-bold text-text-primary mb-2">
                      {item.outcome}
                    </h4>
                    <p className="text-text-secondary mb-3">{item.description}</p>
                    <div className={`inline-flex items-center space-x-2 ${content.color.replace('bg-', 'text-')} text-sm font-semibold`}>
                      <Icon name="ArrowRight" size={14} />
                      <span>{item.impact}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Decision Framework */}
        <div className="card-elevated rounded-2xl p-8 lg:p-12 mb-16 bg-gradient-to-br from-primary/5 to-accent/5">
          <h3 className="text-3xl font-headline font-bold text-text-primary text-center mb-12">
            {content.decisionFramework.title}
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            {content.decisionFramework.questions.map((item, index) => (
              <div key={index} className="bg-background rounded-xl p-6">
                <div className="flex items-start space-x-4 mb-4">
                  <div className={`w-10 h-10 ${content.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <Icon name={item.icon} size={20} color="white" />
                  </div>
                  <h4 className="font-semibold text-text-primary flex-1">{item.question}</h4>
                </div>
                <p className="text-text-secondary ml-14">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Role-Specific CTAs */}
        <div className="text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              variant="default"
              size="lg"
              className={content.color.replace('bg-', 'bg-')}
              iconName="Calendar"
              iconPosition="left"
            >
              {content.cta.primary}
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="FileText"
              iconPosition="left"
            >
              {content.cta.secondary}
            </Button>
            <Button
              variant="ghost"
              size="lg"
              iconName="BookOpen"
              iconPosition="left"
            >
              {content.cta.tertiary}
            </Button>
          </div>
          <p className="text-sm text-text-secondary mt-6">
            Personalized {selectedRole} consultation available within 24 hours
          </p>
        </div>
      </div>
    </section>
  );
};

export default RoleBasedInsights;
